import { Box, Typography, Grid, Chip, Tooltip } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { BaseCard } from '../common/BaseCard';
import { formatDateSafe, formatDocument, cleanDocument } from '../utils/formatters';
import { useParams } from 'react-router-dom';
import { ISocio, ISociosPJ } from '../../../../../../application/models/dossie';
import { useDossieService } from '../../../../../../use-cases/dossie';

export const QuadroSocietarioCard = ({
  socios,
}: {
  socios: ISociosPJ | null;
}) => {
  const { solicitarConsultaComCobranca } = useDossieService();
  const { documento: documentoOrigem } = useParams<{ documento: string }>();
  const sociosList = socios?.socios || [];

  if (sociosList.length === 0) return null;

  return (
    <BaseCard
      title="Quadro Societário"
      icon={<GroupsOutlinedIcon />}
      canSplit={sociosList.length > 2}
    >
      <Box
        className="socio-grid-container"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: sociosList.length > 1 ? '1fr 1fr' : '1fr' },
          gap: 2.5,
        }}
      >
        {sociosList.map((socio: ISocio, idx: number) => {
          const docSocio = socio.DocumentoSocio;
          const cleanDoc = cleanDocument(docSocio);
          const isDocPF = cleanDoc ? cleanDoc.length <= 11 : true;
          const formattedDoc = docSocio ? formatDocument(docSocio, isDocPF) : '-';

          return (
            <Box
              key={idx}
              className="socio-card-item avoid-break"
              sx={{
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid #ece7f2',
                backgroundColor: '#faf8fc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#723989',
                  boxShadow: '0 4px 12px rgba(114, 57, 137, 0.06)',
                  backgroundColor: '#ffffff',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
                <Tooltip title={cleanDoc ? `Consultar dossiê de ${socio.NomeSocio}` : ''} arrow placement="top">
                  <Typography
                    variant="body1"
                    onClick={() =>
                      solicitarConsultaComCobranca(docSocio, socio.IdVelesSocio, documentoOrigem!, {
                        nome: socio.NomeSocio,
                        menorIdade: socio.MenorIdade,
                        pessoaRestrita: socio.PessoaRestrita,
                      })
                    }
                    sx={{
                      color: cleanDoc ? '#723989' : '#1e293b',
                      fontWeight: 700,
                      cursor: cleanDoc ? 'pointer' : 'default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      transition: 'all 0.15s ease',
                      '&:hover': cleanDoc
                        ? {
                            textDecoration: 'underline',
                            color: '#562768',
                          }
                        : {},
                    }}
                  >
                    {socio.NomeSocio}
                    {cleanDoc && (
                      <LaunchIcon className="no-print" sx={{ fontSize: '0.95rem', color: '#723989', opacity: 0.7 }} />
                    )}
                  </Typography>
                </Tooltip>
                {socio.CargoSocio && (
                  <Chip
                    label={socio.CargoSocio}
                    size="small"
                    sx={{ backgroundColor: '#f3eff7', color: '#68457c', fontWeight: 700, borderRadius: '6px', fontSize: '0.75rem' }}
                  />
                )}
              </Box>

              <Grid container spacing={1.5} className="socio-card-grid">
                <Grid item xs={6} sm={6} className="socio-grid-item">
                  <Typography
                    variant="caption"
                    sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                  >
                    Documento
                  </Typography>
                  <Tooltip title={cleanDoc ? `Consultar ${cleanDoc}` : ''} arrow placement="top">
                    <Typography
                      variant="body2"
                      onClick={() =>
                        solicitarConsultaComCobranca(docSocio, socio.IdVelesSocio, documentoOrigem!, {
                          nome: socio.NomeSocio,
                          menorIdade: socio.MenorIdade,
                          pessoaRestrita: socio.PessoaRestrita,
                        })
                      }
                      sx={{
                        color: cleanDoc ? '#723989' : '#1e293b',
                        fontWeight: 600,
                        cursor: cleanDoc ? 'pointer' : 'default',
                        display: 'inline-block',
                        transition: 'all 0.15s ease',
                        '&:hover': cleanDoc
                          ? {
                              textDecoration: 'underline',
                              color: '#562768',
                            }
                          : {},
                      }}
                    >
                      {formattedDoc}
                    </Typography>
                  </Tooltip>
                </Grid>

                {socio.CargoSocio && (
                  <Grid item xs={6} sm={6} className="socio-grid-item">
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                    >
                      Cargo/Função
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {socio.CargoSocio}
                    </Typography>
                  </Grid>
                )}

                {socio.DataSociedade && (
                  <Grid item xs={6} sm={6} className="socio-grid-item">
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                    >
                      Data de Entrada
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {formatDateSafe(socio.DataSociedade)}
                    </Typography>
                  </Grid>
                )}

                {socio.Participacao != null && (
                  <Grid item xs={6} sm={6} className="socio-grid-item">
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, fontWeight: 700 }}
                    >
                      % Participação
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#723989', fontWeight: 700 }}>
                      {socio.Participacao}%
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        })}
      </Box>
    </BaseCard>
  );
};
